import React, { useEffect, useState } from 'react';

interface TimeSlotsProps {
	selectedTime?: string;
	onTimeSelect: (time: string) => void;
	selectedDate?: Date;
	serviceDuration?: number; // Add service duration
}

// Default time slots if Google Calendar integration fails (not for empty results)
const DEFAULT_TIME_SLOTS = [
	'9:00',
	'10:00',
	'11:00',
	'12:00',
	'13:00',
	'14:00',
	'15:00',
];

// Add a helper for formatting the date as YYYY-MM-DD
const formatLocalDateString = (date: Date): string =>
	`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export function TimeSlots({
	selectedTime,
	onTimeSelect,
	selectedDate,
	serviceDuration = 60, // Default to 60 minutes
}: TimeSlotsProps) {
	const [availableSlots, setAvailableSlots] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [statusMessage, setStatusMessage] = useState<string | null>(null);

	useEffect(() => {
		// If we don't have a selected date, just use empty slots
		if (!selectedDate) {
			setAvailableSlots([]);
			setStatusMessage(null);
			return;
		}

		const fetchAvailableSlots = async () => {
			setLoading(true);
			setStatusMessage(null);

			try {
				const baseUrl =
					process.env.NODE_ENV === 'production'
						? 'https://yaap.co.za/.netlify/functions'
						: '/.netlify/functions';
				const response = await fetch(
					`${baseUrl}/available-slots?date=${formatLocalDateString(selectedDate)}&duration=${serviceDuration}`,
				);
				if (!response.ok) {
					throw new Error('Failed to fetch available slots');
				}
				const data = await response.json();

				// Always use the slots returned from API, even if empty
				setAvailableSlots(data.slots);

				// Check for status messages from the API about holidays or OOO
				if (data.message) {
					setStatusMessage(data.message);
				} else if (data.slots.length === 0) {
					setStatusMessage('No available time slots for this date');
				}

				setError(null);
			} catch (err) {
				console.error('Failed to load available time slots:', err);
				setError('Could not load available times');
				// Only use default slots on API error, not for empty results
				setAvailableSlots(DEFAULT_TIME_SLOTS);
			} finally {
				setLoading(false);
			}
		};

		fetchAvailableSlots();
	}, [selectedDate, serviceDuration]); // Add serviceDuration to dependencies

	if (loading) {
		return (
			<div className='text-center py-4 items-center space-x-2'>
				<div className='inline-block h-6 w-6 animate-spin rounded-full border-2 border-solid border-orange-400 border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite] mr-2'></div>
				Loading available time slots...
			</div>
		);
	}

	return (
		<>
			{error && (
				<div className='text-amber-500 text-sm mb-3'>
					{error} - showing default available times
				</div>
			)}
			{availableSlots.length > 0 ? (
				<div className='grid grid-cols-2 gap-3'>
					{availableSlots.map((time) => (
						<button
							key={time}
							className={`p-3 text-center rounded-md border ${
								selectedTime === time
									? 'border-orange-400 bg-orange-50 text-orange-400'
									: 'border-gray-200 hover:border-orange-400'
							}`}
							onClick={() => onTimeSelect(time)}
						>
							{time}
						</button>
					))}
				</div>
			) : (
				<div className='text-center py-8 px-4 border border-amber-200 bg-amber-50 rounded-md'>
					<p className='text-amber-800 font-medium mb-2'>No available slots</p>
					<p className='text-sm text-amber-700'>
						{statusMessage ||
							'There are no available time slots for this date. Please select a different date.'}
					</p>
				</div>
			)}
		</>
	);
}
